<template>
  <v-row class="slide-base">
    <v-col
      :md="primaryMdCols"
      :offset-md="primaryMdOffset"
      :lg="primaryLgCols"
      :offset-lg="primaryLgOffset"
      cols="12"
      ref="primaryCol"
      style="padding:0">
      <transition-group
        name="slideRight"
        v-bind:css="false"
        v-on:before-enter="beforeEnter"
        v-on:enter="enter"
        v-on:leave="leave">
        <template v-for="({ type, id }, index) in primary">
          <component
            v-if="visible"
            :key="'primary-' + index"
            :is="type"
            :section="section"
            :slide-num="slideNum"
            :col-width="primaryWidth"
            :id="id"
            :index="index"
            :primary="true"
            :class="index === 0 ? 'first-item' : (index === primary.length - 1 ? 'last-item' : 'middle-item')"
            class="primary-col"
            @activeFinish="activeFinish({ type, id })"
            @activeWaiting="activeWaiting({ type, id })"></component>
        </template>
      </transition-group>
    </v-col>
    <v-col
      v-if="secondary.length > 0"
      cols="12"
      md="6"
      lg="5"
      ref="secondaryCol"
      style="padding:0">
      <transition-group
        name="slideLeft"
        v-bind:css="false"
        v-on:before-enter="beforeEnter"
        v-on:enter="enter"
        v-on:leave="leave">
        <template v-for="({ type, id }, index) in secondary">
          <component
            v-if="visible"
            :key="'secondary-' + index"
            :is="type"
            :section="section"
            :slide-num="slideNum"
            :col-width="secondaryWidth"
            :id="id"
            :index="index"
            :primary="false"
            :class="index === 0 ? 'first-item' : (index === secondary.length - 1 ? 'last-item' : 'middle-item')"
            class="secondary-col"
            @activeFinish="activeFinish({ type, id })"
            @activeWaiting="activeWaiting({ type, id })"></component>
        </template>
      </transition-group>
    </v-col>
  </v-row>
</template>
<style>
.primary-col, .secondary-col {
  margin: 12px;
}
</style>
<script>
import { ref, computed } from '@vue/composition-api'
import Velocity from 'velocity-animate'
import { hasClass } from '../../helpers/has'
export default {
  components: {
    Agreement: () => import('@/components/slides/agreement'),
    TextCard: () => import('@/components/slides/textCard'),
    ImageCard: () => import('@/components/slides/imageCard'),
    VideoCard: () => import('@/components/slides/videoCard'),
    QuizCard: () => import('@/components/slides/quizCard'),
    Carousel: () => import('@/components/slides/carousel'),
    Popups: () => import('@/components/slides/popups'),
    Timeline: () => import('@/components/slides/timeline')
  },
  props: {
    section: {
      type: String,
      required: true
    },
    slideNum: {
      type: Number,
      required: true
    },
    primary: {
      type: Array,
      required: true
    },
    secondary: {
      type: Array,
      default: () => {
        return []
      }
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  setup (props, { root, emit }) {
    const activeTransitions = ref(0)

    const primaryCol = ref(null)
    const secondaryCol = ref(null)

    const primaryMdCols = computed(() => props.secondary.length > 0 ? 6 : 10)
    const primaryMdOffset = computed(() => props.secondary.length > 0 ? 0 : 1)
    const primaryLgCols = computed(() => props.secondary.length > 0 ? 5 : 8)
    const primaryLgOffset = computed(() => props.secondary.length > 0 ? 1 : 2)

    function beforeEnter (el) {
      let isPrimary = hasClass(el, 'primary-col')
      let isFirst = hasClass(el, 'first-item')
      let isLast = hasClass(el, 'last-item')
      el.style.opacity = 0
      if (isPrimary) {
        if (isFirst) el.style.top = '-300px'
        else el.style.left = '-300px'
      } else {
        if (isLast) el.style.bottom = '-300px'
        else el.style.right = '-300px'
      }
    }
    function enter (el, done) {
      let to = { opacity: 1 }
      let isPrimary = hasClass(el, 'primary-col')
      let isFirst = hasClass(el, 'first-item')
      let isLast = hasClass(el, 'last-item')
      if (isPrimary) {
        if (isFirst) to.top = '0px'
        else to.left = '0px'
      } else {
        if (isLast) to.bottom = '0px'
        else to.right = '0px'
      }
      Velocity(
        el,
        to,
        {
          complete: () => {
            done()
            emit('slideLoaded')
          }
        })
    }
    function leave (el, done) {
      let to = { opacity: 0 }
      let isPrimary = hasClass(el, 'primary-col')
      let isFirst = hasClass(el, 'first-item')
      let isLast = hasClass(el, 'last-item')
      if (isPrimary) {
        if (isFirst) to.top = '-300px'
        else to.left = '-300px'
      } else {
        if (isLast) to.bottom = '-300px'
        else to.right = '-300px'
      }
      activeTransitions.value++
      Velocity(
        el,
        to,
        {
          complete: () => {
            done()
            activeTransitions.value--
            if (activeTransitions.value === 0) {
              emit('out')
            }
          }
        }
      )
    }

    const primaryWidth = computed(() => {
      if (primaryCol.value) return primaryCol.value.offsetWidth - 25
      let width = document.getElementsByTagName('body')[0].offsetWidth - 69
      if (props.secondary.length > 0) {
        if (width >= 1264) {
          return (width / 2 * 0.6666666666) - 24
        } else if (width >= 960) {
          return (width / 2 * 0.8333333333) - 24
        } else {
          return (width / 2) - 24
        }
      } else {
        if (width >= 1264) {
          return (width * 0.6666666666) - 24
        } else if (width >= 960) {
          return (width * 0.8333333333) - 24
        } else {
          return width - 24
        }
      }
    })
    const secondaryWidth = computed(() => {
      if (secondaryCol.value) return secondaryCol.value.offsetWidth - 25
      let width = document.getElementsByTagName('body')[0].offsetWidth - 69
      if (props.secondary.length > 0) {
        if (width >= 1264) {
          return (width / 2 * 0.6666666666) - 24
        } else if (width >= 960) {
          return (width / 2 * 0.8333333333) - 24
        } else {
          return (width / 2) - 24
        }
      } else {
        if (width >= 1264) {
          return (width * 0.6666666666) - 24
        } else if (width >= 960) {
          return (width * 0.8333333333) - 24
        } else {
          return width - 24
        }
      }
    })

    const activeContent = ref([])
    function activeFinish (obj) {
      let types = []
      for (let i = 0; i < activeContent.value.length; i++) {
        if (activeContent.value[i].id === obj.id) activeContent.value.splice(i, 1)
        else types.push(activeContent.value[i].type)
      }
      if (activeContent.value.length === 0) {
        emit('activeFinish')
      } else {
        emit('activeWaiting', { types })
      }
    }
    function activeWaiting (obj) {
      let exists = false
      let types = []
      for (const { id, type } in activeContent.value) {
        if (obj.id === id) exists = true
        types.push(type)
      }
      if (!exists) {
        activeContent.value.push(obj)
        types.push(obj.type)
      }
      emit('activeWaiting', { types })
    }

    return {
      primaryCol,
      secondaryCol,
      primaryMdCols,
      primaryMdOffset,
      primaryLgCols,
      primaryLgOffset,
      primaryWidth,
      secondaryWidth,
      beforeEnter,
      enter,
      leave,
      activeContent,
      activeFinish,
      activeWaiting
    }
  }
}
</script>
